import { useState } from 'react';
import { api } from '../api';
import { useToast, InvoiceBadge, formatDate } from './shared';

const money = v => (v || v === 0) ? `${Number(v).toLocaleString('en')} EGP` : '—';
export const INVOICE_KIND = { visit_fee: 'رسوم الزيارة', parts: 'قطع غيار', repair: 'إصلاح', other: 'أخرى' };

// Repair + installed parts for one device inside a visit (stored as a normal ticket linked to the visit)
export function DeviceRepair({ visitId, cd, canEdit, onChange, onOpenTicket }) {
  const r = cd.repair;
  const [form, setForm] = useState(null); // create/edit repair
  const [part, setPart] = useState({ name: '', quantity: 1, unit_price: '', warranty_months: '' });
  const toast = useToast();
  const run = async p => { try { onChange(await p); } catch (e) { toast(e.message, 'error'); } };

  if (!r && !form) {
    return canEdit ? <button className="btn btn-ghost btn-sm mt-8" onClick={() => setForm({ fault_description: '', notes: '', outcome: 'onsite' })}>🔧 إصلاح / قطع غيار</button> : null;
  }
  if (form) {
    return (
      <div className="mt-8" style={{ padding: 10, background: 'var(--bg2)', borderRadius: 8 }}>
        <input className="input mb-8" placeholder="العطل / اللي اتصلح" value={form.fault_description} onChange={e => setForm({ ...form, fault_description: e.target.value })} autoFocus />
        <textarea className="input mb-8" rows={2} placeholder="ملاحظات المهندس" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
        <div className="flex gap-8" style={{ flexWrap: 'wrap' }}>
          <button className={`btn btn-sm ${form.outcome === 'onsite' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setForm({ ...form, outcome: 'onsite' })}>✓ اتصلح في الموقع</button>
          <button className={`btn btn-sm ${form.outcome === 'workshop' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setForm({ ...form, outcome: 'workshop' })}>🚚 اتسحب للورشة</button>
          <span style={{ flex: 1 }} />
          <button className="btn btn-primary btn-sm" onClick={() => run((r ? api.pmUpdateRepair(r.id, form) : api.pmAddRepair(visitId, { ...form, contract_device_id: cd.id }))).then(() => setForm(null))}>حفظ</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setForm(null)}>إلغاء</button>
        </div>
      </div>
    );
  }
  const total = r.parts.reduce((s, p) => s + (p.unit_price || 0) * (p.quantity || 1), 0);
  return (
    <div className="mt-8" style={{ padding: 10, background: 'var(--bg2)', borderRadius: 8, borderInlineStart: '3px solid var(--yellow)' }}>
      <div className="flex items-center gap-8" style={{ flexWrap: 'wrap' }}>
        <span className="fw-600 text-sm">🔧 {r.fault_description}</span>
        <span className={`badge ${r.status === 'delivered' ? 'badge-green' : 'badge-yellow'}`}>{r.status === 'delivered' ? 'اتصلح في الموقع' : 'في الورشة'}</span>
        <span style={{ flex: 1 }} />
        {canEdit && <button className="btn btn-ghost btn-sm" onClick={() => setForm({ fault_description: r.fault_description || '', notes: r.notes || '', outcome: r.status === 'delivered' ? 'onsite' : 'workshop' })}>تعديل</button>}
        {onOpenTicket && <button className="btn btn-ghost btn-sm" onClick={() => onOpenTicket(r.id)}>التذكرة ←</button>}
      </div>
      {r.notes && <div className="text-xs text-muted mt-4">{r.notes}</div>}
      <div className="mt-8">
        {r.parts.map(p => (
          <div key={p.id} className="flex items-center gap-8 text-sm" style={{ padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ flex: 1 }}>⚙️ {p.name} <span className="text-dim">×{p.quantity}</span>{p.warranty_months ? <span className="text-xs text-dim"> · ضمان {p.warranty_months} شهر</span> : null}</span>
            <span className="mono text-xs">{p.unit_price ? money(p.unit_price * (p.quantity || 1)) : ''}</span>
            {canEdit && <button className="btn btn-ghost btn-sm" style={{ padding: '0 6px' }} onClick={() => run(api.pmDeleteRepairPart(p.id))}>✕</button>}
          </div>
        ))}
        {total > 0 && <div className="text-xs fw-600 mt-4">إجمالي القطع: {money(total)}</div>}
      </div>
      {canEdit && (
        <div className="mt-8" style={{ display: 'grid', gridTemplateColumns: '2fr 60px 90px 80px auto', gap: 6 }}>
          <input className="input" placeholder="قطعة اتركبت" value={part.name} onChange={e => setPart({ ...part, name: e.target.value })} />
          <input className="input" type="number" min="1" title="الكمية" value={part.quantity} onChange={e => setPart({ ...part, quantity: e.target.value })} />
          <input className="input" type="number" placeholder="السعر" value={part.unit_price} onChange={e => setPart({ ...part, unit_price: e.target.value })} />
          <input className="input" type="number" placeholder="ضمان/شهر" value={part.warranty_months} onChange={e => setPart({ ...part, warranty_months: e.target.value })} />
          <button className="btn btn-primary btn-sm" disabled={!part.name.trim()}
            onClick={() => run(api.pmAddRepairPart(r.id, part)).then(() => setPart({ name: '', quantity: 1, unit_price: '', warranty_months: '' }))}>+</button>
        </div>
      )}
    </div>
  );
}

// Invoices issued for a visit (visit fee, parts, extra repair)
export function VisitInvoices({ v, isAdmin, onChange }) {
  const [f, setF] = useState(null);
  const toast = useToast();
  const total = v.invoices.reduce((s, i) => s + (i.amount || 0), 0);
  const paid = v.invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.amount || 0), 0);
  async function save() {
    try { onChange(await api.pmAddVisitInvoice(v.id, f)); setF(null); } catch (e) { toast(e.message, 'error'); }
  }
  async function setStatus(inv, status) {
    try { await api.updateInvoice(inv.id, { status }); onChange(await api.pmVisit(v.id)); } catch (e) { toast(e.message, 'error'); }
  }
  async function del(inv) {
    if (!window.confirm('حذف الفاتورة؟')) return;
    await api.deleteInvoice(inv.id); onChange(await api.pmVisit(v.id));
  }
  const next = { issued: 'sent', sent: 'paid' };
  return (
    <div className="card mb-16" style={{ padding: 14 }}>
      <div className="flex items-center gap-8" style={{ flexWrap: 'wrap' }}>
        <span className="fw-600">🧾 فواتير الزيارة</span>
        <span className="text-xs text-muted">{money(total)}{total ? ` · اتدفع ${money(paid)}` : ''}</span>
        <span style={{ flex: 1 }} />
        {isAdmin && !f && <button className="btn btn-primary btn-sm" onClick={() => setF({
          kind: v.parts_total ? 'parts' : 'visit_fee', invoice_number: '', amount: v.parts_total || '', notes: '' })}>+ فاتورة</button>}
      </div>
      {v.parts_total > 0 && <div className="text-xs text-dim mt-4">قيمة القطع المتركبة في الزيارة: {money(v.parts_total)}</div>}
      {f && (
        <div className="mt-8" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
          <select className="select" value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })}>
            {Object.entries(INVOICE_KIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input className="input" placeholder="رقم الفاتورة" value={f.invoice_number} onChange={e => setF({ ...f, invoice_number: e.target.value })} />
          <input className="input" type="number" placeholder="المبلغ" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} />
          <input className="input" placeholder="ملاحظات" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} />
          <div className="flex gap-8"><button className="btn btn-primary btn-sm" onClick={save}>حفظ</button><button className="btn btn-ghost btn-sm" onClick={() => setF(null)}>إلغاء</button></div>
        </div>
      )}
      {v.invoices.map(inv => (
        <div key={inv.id} className="flex items-center gap-8 text-sm mt-8" style={{ flexWrap: 'wrap', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
          <span className="mono fw-600">{inv.invoice_number || '—'}</span>
          <span className="text-muted">{INVOICE_KIND[inv.kind] || inv.kind}</span>
          <span className="mono">{money(inv.amount)}</span>
          <InvoiceBadge status={inv.status} />
          <span className="text-xs text-dim">{formatDate(inv.issued_at)}</span>
          {inv.notes && <span className="text-xs text-muted">{inv.notes}</span>}
          <span style={{ flex: 1 }} />
          {isAdmin && next[inv.status] && <button className="btn btn-ghost btn-sm" onClick={() => setStatus(inv, next[inv.status])}>→ {next[inv.status] === 'sent' ? 'Sent' : 'Paid'}</button>}
          {isAdmin && <button className="btn btn-danger btn-sm" onClick={() => del(inv)}>حذف</button>}
        </div>
      ))}
      {!v.invoices.length && !f && <div className="text-xs text-dim mt-8">لسه مفيش فواتير للزيارة دي</div>}
    </div>
  );
}
